import type { QueryClient } from '@tanstack/react-query';

import { librarySectionsQueryOptions } from '@/hooks/queries/useLibrarySectionsQuery';

import createShareLink from './api/createShareLink';

import type { LibrarySection } from '@/types';
import type { PresetShareTarget } from './shareTarget';

// Slug alone: a link also depends on the surface and on native-vs-desktop,
// neither of which can change within a session.
const shareLinkKey = (slug: string) => ['preset-share-link', slug];

/**
 * Cached link, read synchronously — the OS share sheet and the clipboard both
 * need the tap's transient activation, which awaiting anything would spend.
 */
export const getCachedShareLink = ({
  queryClient,
  slug,
}: {
  queryClient: QueryClient;
  slug: string;
}): string | undefined => queryClient.getQueryData<string>(shareLinkKey(slug));

/**
 * A link is immutable for the session, hence `staleTime: Infinity` — a re-share
 * costs no request. `createShareLink` reports failure as `null`, which React
 * Query would cache as a success, so it is rethrown for retries and a refetch on
 * next access, as `useProjectCredits` does.
 */
export const fetchShareLink = ({
  queryClient,
  target,
}: {
  queryClient: QueryClient;
  target: PresetShareTarget;
}): Promise<string> =>
  queryClient.fetchQuery({
    queryKey: shareLinkKey(target.slug),
    queryFn: async () => {
      const category = queryClient
        .getQueryData<LibrarySection[]>(librarySectionsQueryOptions.queryKey)
        ?.find((section) =>
          section.items.some((item) => item.slug === target.slug)
        )?.slug;
      const link = await createShareLink({ ...target, category });
      if (!link) throw new Error('Failed to create a preset share link');
      return link;
    },
    // One retry only: `createShareLink` collapses a 400 and a dropped
    // connection into the same `null`, so extra attempts mostly delay the error
    // toast on failures that will never succeed.
    retry: 1,
    retryDelay: 300,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });
